import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  UserMinus2, Plus, ChevronDown, ChevronUp, Calculator, Check,
  ShieldCheck, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const TYPE_LABELS = {
  renuncia_voluntaria: "Renuncia voluntaria",
  termino_contrato: "Término de contrato",
  rescision_justificada: "Rescisión justificada",
  rescision_injustificada: "Rescisión injustificada",
  mutuo_acuerdo: "Mutuo acuerdo",
  jubilacion: "Jubilación",
  muerte: "Muerte",
};

const STATUS_CONFIG = {
  borrador:   { label: "Borrador",   color: "bg-slate-100 text-slate-700" },
  calculado:  { label: "Calculado",  color: "bg-blue-100 text-blue-800" },
  autorizado: { label: "Autorizado", color: "bg-emerald-100 text-emerald-800" },
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

export default function OpsRRHHBajas() {
  const { user } = useAuth();
  const [terminations, setTerminations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ employee_id: "", termination_date: new Date().toISOString().slice(0, 10), termination_type: "renuncia_voluntaria", reason_detail: "" });
  const [busyAction, setBusyAction] = useState(null);

  const canAuthorize = user?.role === "corporate_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ops/terminations");
      setTerminations(r.data || []);
    } catch {
      toast.error("Error al cargar bajas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/ops/employees?status=alta&limit=200").then(r => setEmployees(r.data.employees || [])).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.employee_id) { toast.error("Elige un colaborador"); return; }
    setSaving(true);
    try {
      const r = await api.post("/ops/terminations", form);
      toast.success("Baja registrada");
      setShowNew(false);
      setForm({ employee_id: "", termination_date: new Date().toISOString().slice(0, 10), termination_type: "renuncia_voluntaria", reason_detail: "" });
      await load();
      setExpandedId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar la baja");
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = async (id) => {
    setBusyAction(`calc-${id}`);
    try {
      await api.post(`/ops/terminations/${id}/calculate-finiquito`);
      toast.success("Finiquito calculado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al calcular finiquito");
    } finally {
      setBusyAction(null);
    }
  };

  const handlePatch = async (id, patch) => {
    setBusyAction(`patch-${id}`);
    try {
      await api.patch(`/ops/terminations/${id}`, patch);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally {
      setBusyAction(null);
    }
  };

  const handleUpload = async (id, docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("El archivo no puede superar 8 MB"); return; }
    setBusyAction(`upload-${docType}-${id}`);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/ops/terminations/${id}/documents`, { doc_type: docType, filename: file.name, file_base64: base64 });
      toast.success("Documento subido");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir el documento");
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async (id, docType) => {
    try {
      const r = await api.get(`/ops/terminations/${id}/documents/${docType}/file`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(r.data), "_blank");
    } catch {
      toast.error("Documento no disponible");
    }
  };

  const handleAuthorize = async (id) => {
    if (!window.confirm("¿Autorizar esta baja? El colaborador dejará de aparecer en la próxima nómina.")) return;
    setBusyAction(`auth-${id}`);
    try {
      await api.post(`/ops/terminations/${id}/authorize`);
      toast.success("Baja autorizada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al autorizar");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="bajas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserMinus2 className="w-6 h-6 text-[#FFDF00]" />
            Bajas y finiquito
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{terminations.length} baja{terminations.length !== 1 ? "s" : ""} registrada{terminations.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="new-termination-btn" size="sm" onClick={() => setShowNew(true)} className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white">
          <Plus className="w-4 h-4" /> Nueva baja
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Cargando...</div>
      ) : terminations.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-bajas">
          <UserMinus2 className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Sin bajas registradas</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-testid="terminations-table">
          {terminations.map(t => {
            const isOpen = expandedId === t.id;
            const st = STATUS_CONFIG[t.status] || STATUS_CONFIG.borrador;
            return (
              <div key={t.id} data-testid={`termination-row-${t.id}`}>
                <button
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                  onClick={() => setExpandedId(isOpen ? null : t.id)}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{t.employee_name}</p>
                      <p className="text-xs text-slate-500">{TYPE_LABELS[t.termination_type] || t.termination_type} · {t.termination_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {t.finiquito_amount != null && <span className="text-sm font-semibold text-slate-700">{money(t.finiquito_amount)}</span>}
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-5 pt-1 bg-slate-50 border-t border-slate-100 space-y-4" data-testid={`termination-detail-${t.id}`}>
                    {t.reason_detail && <p className="text-sm text-slate-600">Motivo: {t.reason_detail}</p>}

                    {/* Finiquito */}
                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><Calculator className="w-4 h-4" /> Finiquito</h4>
                        <Button
                          size="sm" variant="outline" className="text-xs gap-1"
                          data-testid={`calc-finiquito-btn-${t.id}`}
                          disabled={busyAction === `calc-${t.id}`}
                          onClick={() => handleCalculate(t.id)}
                        >
                          {busyAction === `calc-${t.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
                          {t.finiquito_breakdown ? "Recalcular" : "Calcular finiquito"}
                        </Button>
                      </div>
                      {t.finiquito_breakdown ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm" data-testid={`finiquito-breakdown-${t.id}`}>
                          {Object.entries({
                            "Sueldo pendiente": t.finiquito_breakdown.sueldo_pendiente,
                            "Aguinaldo prop.": t.finiquito_breakdown.aguinaldo_proporcional,
                            "Vacaciones pend.": t.finiquito_breakdown.vacaciones_pendientes,
                            "Prima vacacional": t.finiquito_breakdown.prima_vacacional,
                            "Prima antigüedad": t.finiquito_breakdown.prima_antiguedad,
                            "Indemnización": (t.finiquito_breakdown.indemnizacion_constitucional || 0) + (t.finiquito_breakdown.indemnizacion_20_dias_por_anio || 0),
                          }).map(([k, v]) => (
                            <div key={k} className="text-slate-600">{k}: <span className="font-medium text-slate-900">{money(v)}</span></div>
                          ))}
                          <div className="col-span-2 sm:col-span-3 pt-2 border-t border-slate-100 font-bold text-slate-900">
                            Total: {money(t.finiquito_breakdown.total)}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">Aún no calculado</p>
                      )}
                    </div>

                    {/* Checklist */}
                    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
                      <h4 className="font-semibold text-sm text-slate-700">Checklist de cierre</h4>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid={`exit-interview-checkbox-${t.id}`} checked={!!t.exit_interview_completed}
                          onChange={e => handlePatch(t.id, { exit_interview_completed: e.target.checked })} className="w-4 h-4" />
                        Entrevista de salida completada
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid={`imss-baja-checkbox-${t.id}`} checked={!!t.imss_baja_processed}
                          onChange={e => handlePatch(t.id, { imss_baja_processed: e.target.checked })} className="w-4 h-4" />
                        Baja IMSS procesada {t.imss_baja_date && `(${t.imss_baja_date})`}
                      </label>

                      <div className="flex items-center gap-3 flex-wrap pt-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Renuncia voluntaria:</span>
                          {t.renuncia_voluntaria_url ? (
                            <button className="text-xs font-semibold text-slate-600 hover:underline" onClick={() => handleDownload(t.id, "renuncia_voluntaria")}>Ver documento</button>
                          ) : (
                            <label className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer">
                              Subir
                              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`upload-renuncia-${t.id}`}
                                onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(t.id, "renuncia_voluntaria", f); }} />
                            </label>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Comprobante finiquito:</span>
                          {t.finiquito_url ? (
                            <button className="text-xs font-semibold text-slate-600 hover:underline" onClick={() => handleDownload(t.id, "finiquito")}>Ver documento</button>
                          ) : (
                            <label className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer">
                              Subir
                              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`upload-finiquito-${t.id}`}
                                onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(t.id, "finiquito", f); }} />
                            </label>
                          )}
                        </div>
                      </div>

                      <label className={`flex items-center gap-2 text-sm ${t.finiquito_amount ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                        <input type="checkbox" data-testid={`finiquito-paid-checkbox-${t.id}`} checked={!!t.finiquito_paid} disabled={!t.finiquito_amount}
                          onChange={e => handlePatch(t.id, { finiquito_paid: e.target.checked })} className="w-4 h-4" />
                        Finiquito pagado {t.finiquito_paid_at && `(${t.finiquito_paid_at.slice(0, 10)})`}
                      </label>
                    </div>

                    {/* Autorización */}
                    {t.status === "autorizado" ? (
                      <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium">
                        <Check className="w-4 h-4" /> Baja autorizada — el colaborador ya no aparece en nómina
                      </div>
                    ) : canAuthorize ? (
                      <Button
                        size="sm" data-testid={`authorize-termination-btn-${t.id}`}
                        disabled={!t.finiquito_breakdown || busyAction === `auth-${t.id}`}
                        onClick={() => handleAuthorize(t.id)}
                        className="gap-1.5 bg-[#FFDF00] text-slate-900 hover:bg-yellow-400"
                      >
                        <ShieldCheck className="w-4 h-4" /> Autorizar baja
                      </Button>
                    ) : (
                      <p className="text-xs text-slate-400">Sólo Dirección/Corporativo puede autorizar la baja.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal nueva baja */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="new-termination-modal">
            <h3 className="font-bold text-slate-900">Nueva baja</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Colaborador</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.employee_id}
                onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} data-testid="termination-employee-select">
                <option value="">Seleccionar...</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.position})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de baja</label>
              <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.termination_date}
                onChange={e => setForm(f => ({ ...f, termination_date: e.target.value }))} data-testid="termination-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de baja</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.termination_type}
                onChange={e => setForm(f => ({ ...f, termination_type: e.target.value }))} data-testid="termination-type-select">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Detalle (opcional)</label>
              <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} value={form.reason_detail}
                onChange={e => setForm(f => ({ ...f, reason_detail: e.target.value }))} data-testid="termination-reason-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowNew(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleCreate} disabled={saving}
                className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50"
                data-testid="confirm-new-termination-btn">
                {saving ? "Guardando..." : "Registrar baja"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
